/**
 * Split Export button + template/destination menu.
 *
 * Moved out of ArtifactDetailPage (spec 29 §8) so the response overview and
 * the full response reader can share one export control. Behaviour is
 * unchanged; the SharePoint group is only rendered when a handler is passed.
 *
 * Template check (R3 T1, D4): a download with a template whose export would
 * replace part of itself stops here with a warning first, from its stored
 * check report. A template uploaded before the check existed, and Save to
 * SharePoint, are stopped by the server's 409 instead, which the caller hands
 * to the app-wide `TemplateIssueHost` (`routeTemplateExportError`).
 *
 * Word or PDF (R3 T2): with an `artifactId`, the menu offers a format (Word is
 * the default). A PDF is converted from the exact Word file of the export, so
 * it takes a moment; the line beside the button shows progress, then the
 * result ("24 pages · Aptos shown as Carlito") with the download, or the
 * failure with "Try again". The template check applies to a PDF exactly as to
 * Word: the chosen format travels with `onExport`'s options.
 */

import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { ChevronDown, Download, Loader2, Settings, Star } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { SourceIcon } from "@/components/ui/source-icon"
import { TemplateIssueDialog } from "@/components/TemplateIssueDialog"
import { useExportTemplatesStore } from "@/stores/export-templates-store"
import { usePdfExportStore } from "@/stores/pdf-export-store"
import { templateNeedsAttention, type TemplateIssue } from "@/lib/template-check"
import { pdfResultLine, withoutTryAgain } from "@/lib/output-version-text"
import type { ExportFormat, ExportOptions } from "@/lib/api"

function isExportFormat(value: string): value is ExportFormat {
  return value === "docx" || value === "pdf"
}

/** Progress, then the result or the failure, of this document's latest PDF. */
function PdfResult({ artifactId }: { artifactId: string }) {
  const entry = usePdfExportStore((s) => s.entries[artifactId])
  const retryPdf = usePdfExportStore((s) => s.retryPdf)
  const downloadPdf = usePdfExportStore((s) => s.downloadPdf)
  if (!entry) return null

  if (entry.phase === "converting") {
    return (
      <span className="text-meta inline-flex items-center gap-1.5" role="status">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Making PDF…
      </span>
    )
  }

  if (entry.phase === "ready") {
    const line = pdfResultLine(entry.version)
    return (
      <span className="text-meta inline-flex items-center gap-2" role="status">
        {line && <span>{line}</span>}
        <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => void downloadPdf(artifactId)}>
          Download PDF
        </Button>
      </span>
    )
  }

  return (
    <span className="text-meta inline-flex max-w-[22rem] flex-wrap items-center justify-end gap-x-2 text-right" role="alert">
      <span className="text-destructive">{entry.parentId ? withoutTryAgain(entry.error) : entry.error}</span>
      {entry.parentId && (
        <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => void retryPdf(artifactId)}>
          Try again
        </Button>
      )}
    </span>
  )
}


export function ExportDropdown({
  artifactId,
  disabled,
  isExporting,
  onExport,
  onSaveToSharePoint,
  isSharePointConnected,
}: {
  /** The document exported. When given, the menu offers PDF beside Word. */
  artifactId?: string | undefined
  disabled: boolean
  isExporting: boolean
  onExport: (templateId?: string, options?: ExportOptions) => Promise<void>
  onSaveToSharePoint?: (templateId?: string) => void | Promise<void>
  isSharePointConnected?: boolean
}) {
  const navigate = useNavigate()
  const { templates, fetchTemplates } = useExportTemplatesStore()
  const [open, setOpen] = useState(false)
  const [issue, setIssue] = useState<TemplateIssue | null>(null)
  const [format, setFormat] = useState<ExportFormat>("docx")
  const offersPdf = !!artifactId
  const isPdf = offersPdf && format === "pdf"
  // Word exports keep their exact old call shape; a PDF adds the format.
  const formatOptions: ExportOptions | undefined = isPdf ? { format: "pdf" } : undefined

  const runExport = (templateId?: string) => {
    if (formatOptions) void onExport(templateId, formatOptions)
    else if (templateId) void onExport(templateId)
    else void onExport()
  }

  /** Download, unless the template's check says to stop and ask first. */
  const exportWithTemplate = (templateId: string) => {
    const template = templates.find((t) => t.id === templateId)
    if (template && templateNeedsAttention(template.checkReport)) {
      setIssue({ templateId: template.id, templateName: template.name, report: template.checkReport })
      return
    }
    runExport(templateId)
  }

  const continueIssue = () => {
    if (!issue) return
    setIssue(null)
    void onExport(issue.templateId, { ...formatOptions, acknowledgeTemplateLoss: true })
  }

  // Lazy-fetch templates when dropdown opens
  useEffect(() => {
    if (open) {
      void fetchTemplates()
    }
  }, [open, fetchTemplates])

  const defaultTemplate = templates.find((t) => t.isDefault)

  const handleButtonClick = () => {
    if (defaultTemplate) {
      exportWithTemplate(defaultTemplate.id)
    } else {
      runExport()
    }
  }

  return (
    // The PDF line sits UNDER the buttons, so a long failure never squeezes
    // the document title beside them.
    <div className="flex flex-col items-end gap-1">
      <div className="flex items-center">
        <Button
          variant="outline"
          size="sm"
          onClick={handleButtonClick}
          disabled={disabled}
          className="rounded-r-none border-r-0"
        >
          {isExporting ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          {isPdf ? "Export PDF" : "Export"}
        </Button>
        <DropdownMenu open={open} onOpenChange={setOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              disabled={disabled}
              className="rounded-l-none px-1.5"
              aria-label="More export options"
            >
              <ChevronDown className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {offersPdf && (
              <>
                <div className="px-2 py-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                  Format
                </div>
                <DropdownMenuRadioGroup
                  value={format}
                  onValueChange={(value) => {
                    if (isExportFormat(value)) setFormat(value)
                  }}
                >
                  {/* Choosing a format keeps the menu open for the template choice. */}
                  <DropdownMenuRadioItem value="docx" onSelect={(e) => e.preventDefault()}>
                    Word
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="pdf" onSelect={(e) => e.preventDefault()}>
                    PDF
                  </DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem onClick={() => runExport()}>
              Plain (no template)
            </DropdownMenuItem>
            {templates.length > 0 && (
              <>
                <DropdownMenuSeparator />
                {templates.map((t) => (
                  <DropdownMenuItem
                    key={t.id}
                    onClick={() => exportWithTemplate(t.id)}
                  >
                    <span className="truncate">{t.name}</span>
                    {t.isDefault && (
                      <Star className="h-3 w-3 ml-auto shrink-0 fill-amber-500 text-amber-500" />
                    )}
                  </DropdownMenuItem>
                ))}
              </>
            )}
            {onSaveToSharePoint && (
              <>
                <DropdownMenuSeparator />
                <div className="px-2 py-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                  Save to SharePoint
                </div>
                {isSharePointConnected ? (
                  <DropdownMenuItem onClick={() => void onSaveToSharePoint(defaultTemplate?.id)}>
                    <SourceIcon source="sharepoint" size={16} className="mr-2" />
                    {defaultTemplate
                      ? `Using "${defaultTemplate.name}"`
                      : 'Plain (no template)'}
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    onClick={() => void navigate('/app/settings/integrations')}
                  >
                    <SourceIcon source="sharepoint" size={16} className="mr-2" />
                    Connect SharePoint to enable
                  </DropdownMenuItem>
                )}
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => void navigate('/app/settings/export-templates')}
            >
              <Settings className="h-4 w-4 mr-2" />
              {templates.length > 0 ? 'Manage Templates' : 'Upload Template'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <TemplateIssueDialog issue={issue} onContinue={continueIssue} onClose={() => setIssue(null)} />
      </div>
      {artifactId && <PdfResult artifactId={artifactId} />}
    </div>
  )
}
